/**
 * Pure state machine for a single voice session.
 *
 * The reducer has no DOM or WebRTC dependencies — it can be unit-tested by
 * driving events through `voiceReducer` and checking the resulting state.
 *
 * The companion `handleRealtimeMessage` parses an OpenAI Realtime data-channel
 * message and dispatches reducer events plus side effects (tool dispatch,
 * sending follow-up events back over the data channel). Side effects are
 * delegated to the injected `RealtimeMessageContext` so the function is
 * testable with a plain in-memory mock.
 */

/**
 * Lifecycle of a voice session, in order:
 * - `idle` — no session (the starting and stopped state).
 * - `connecting` — establishing the WebRTC connection.
 * - `listening` — connected and capturing the user's speech.
 * - `thinking` — the model is processing / generating a response.
 * - `speaking` — the assistant is playing audio back.
 * - `error` — the session failed; see {@link VoiceMachine.errorMessage}.
 */
export type VoiceState = "idle" | "connecting" | "listening" | "thinking" | "speaking" | "error";

export type VoiceEvent =
  | { type: "start" }
  | { type: "connected" }
  /** An assistant-initiated turn was requested (e.g. the opening greeting). */
  | { type: "response_requested" }
  | { type: "speech_started" }
  | { type: "speech_stopped" }
  | { type: "audio_delta" }
  | { type: "audio_stopped" }
  | { type: "stop" }
  | { type: "error"; message: string };

/** Snapshot of a voice session's state machine. */
export interface VoiceMachine {
  /** Current lifecycle state of the session. */
  state: VoiceState;
  /** Human-readable failure reason — set only when `state === "error"`. */
  errorMessage?: string;
}

export const INITIAL_VOICE_MACHINE: VoiceMachine = { state: "idle" };

export function voiceReducer(machine: VoiceMachine, event: VoiceEvent): VoiceMachine {
  switch (event.type) {
    case "start":
      return machine.state === "idle" ? { state: "connecting" } : machine;
    case "connected":
      return machine.state === "connecting" ? { state: "listening" } : machine;
    case "response_requested":
      // The assistant is generating an unprompted turn (the opening greeting).
      // Move to `thinking` so the subsequent `audio_delta` lands on `speaking`,
      // matching a normal turn; no-op if we're not idling in `listening`.
      return machine.state === "listening" ? { state: "thinking" } : machine;
    case "speech_started":
      if (machine.state === "idle" || machine.state === "error") return machine;
      return { state: "listening" };
    case "speech_stopped":
      return machine.state === "listening" ? { state: "thinking" } : machine;
    case "audio_delta":
      if (machine.state === "thinking" || machine.state === "speaking") {
        return { state: "speaking" };
      }
      return machine;
    case "audio_stopped":
      return machine.state === "speaking" ? { state: "listening" } : machine;
    case "stop":
      return { state: "idle" };
    case "error":
      return { state: "error", errorMessage: event.message };
    default: {
      const _exhaustive: never = event;
      void _exhaustive;
      return machine;
    }
  }
}

/**
 * Per-`response.done` token usage emitted by OpenAI's Realtime API.
 * The SDK accumulates these across the session and ships the totals to the
 * `session-event` stop endpoint so billing has the dimensions it needs.
 */
export interface RealtimeResponseUsage {
  /** Total input tokens for this response (text + audio combined). */
  inputTokens?: number;
  /** Cached input tokens — subset of `inputTokens`. */
  cachedInputTokens?: number;
  /** Total output tokens for this response (text + audio combined). */
  outputTokens?: number;
  /** Audio-input tokens (subset of `inputTokens`). */
  audioInputTokens?: number;
  /** Audio-output tokens (subset of `outputTokens`). */
  audioOutputTokens?: number;
  /** Text-input tokens (subset of `inputTokens`). */
  textInputTokens?: number;
  /** Text-output tokens (subset of `outputTokens`). */
  textOutputTokens?: number;
}

/**
 * Transcript-side events the session uses to persist a voice conversation in
 * the same shape as text chat. Distinct from the state-machine `VoiceEvent`s:
 * these carry text + ordering, not lifecycle state.
 *
 * - `user_turn` — a user utterance just finished (VAD `speech_stopped`). Emitted
 *   *before* the user's transcript completes (which is async and can land after
 *   the assistant has already replied), so the session reserves an ordered slot
 *   here and back-fills the text when the `transcript` event arrives.
 * - `user_transcript_failed` — input transcription failed for an utterance; the
 *   reserved slot must be voided so it doesn't block later turns.
 * - `transcript` — a settled user/assistant transcript, keyed by the OpenAI
 *   conversation `item_id` (stable; used to derive the message id + dedupe).
 */
export type VoiceTranscriptEvent =
  | { kind: "user_turn" }
  | { kind: "user_transcript_failed" }
  | { kind: "transcript"; itemId: string; role: "user" | "assistant"; text: string };

export interface RealtimeMessageContext {
  send: (event: VoiceEvent) => void;
  sendData: (payload: unknown) => void;
  dispatchToolCall: (name: string, args: unknown) => Promise<unknown>;
  isDispatched: (callId: string) => boolean;
  markDispatched: (callId: string) => void;
  /** Forward a per-response usage payload to the session for accumulation. */
  recordUsage?: (usage: RealtimeResponseUsage) => void;
  /** Forward transcript/ordering events so the session can persist the conversation. */
  onTranscriptEvent?: (event: VoiceTranscriptEvent) => void;
}

interface RealtimeFunctionCallItem {
  type: "function_call";
  call_id?: string;
  name?: string;
  arguments?: string;
}

interface RealtimeResponseDoneItem {
  type?: string;
  call_id?: string;
  name?: string;
  arguments?: string;
}

interface RealtimeUsageTokenDetails {
  cached_tokens?: number;
  audio_tokens?: number;
  text_tokens?: number;
}

interface RealtimeUsage {
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  input_token_details?: RealtimeUsageTokenDetails;
  output_token_details?: RealtimeUsageTokenDetails;
}

interface RealtimeResponseDone {
  output?: RealtimeResponseDoneItem[];
  usage?: RealtimeUsage;
}

interface RealtimeMessage {
  type?: string;
  response?: RealtimeResponseDone;
  error?: { message?: string };
  /** Conversation item id — present on transcript-completion messages. */
  item_id?: string;
  /** Final transcript text — present on `*.completed` / `*.transcript.done`. */
  transcript?: string;
}

/** Emit a settled transcript turn, skipping empties. Best-effort; never throws. */
function emitTranscript(
  ctx: RealtimeMessageContext,
  role: "user" | "assistant",
  itemId: string | undefined,
  transcript: string | undefined
): void {
  if (!ctx.onTranscriptEvent) return;
  const text = (transcript ?? "").trim();
  if (!itemId || !text) return;
  try {
    ctx.onTranscriptEvent({ kind: "transcript", itemId, role, text });
  } catch {
    // Persistence wiring is best-effort; never let it break the session loop.
  }
}

function isFunctionCall(item: RealtimeResponseDoneItem): item is RealtimeFunctionCallItem {
  return item.type === "function_call";
}

function parseToolArgs(raw: string | undefined): unknown {
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

async function dispatchFunctionCall(
  call: RealtimeFunctionCallItem,
  ctx: RealtimeMessageContext
): Promise<void> {
  const callId = call.call_id;
  const name = call.name;
  if (!callId || !name) return;
  if (ctx.isDispatched(callId)) return;
  ctx.markDispatched(callId);

  const args = parseToolArgs(call.arguments);

  let output: string;
  try {
    const result = await ctx.dispatchToolCall(name, args);
    output = JSON.stringify(result ?? null);
  } catch (error) {
    output = JSON.stringify({
      error: error instanceof Error ? error.message : "Tool execution failed",
    });
  }

  ctx.sendData({
    type: "conversation.item.create",
    item: { type: "function_call_output", call_id: callId, output },
  });
  ctx.sendData({ type: "response.create" });
}

function extractUsage(raw: RealtimeUsage | undefined): RealtimeResponseUsage | null {
  if (!raw) return null;
  const usage: RealtimeResponseUsage = {};
  if (typeof raw.input_tokens === "number") usage.inputTokens = raw.input_tokens;
  if (typeof raw.output_tokens === "number") usage.outputTokens = raw.output_tokens;
  const inDetails = raw.input_token_details;
  if (inDetails) {
    if (typeof inDetails.cached_tokens === "number") {
      usage.cachedInputTokens = inDetails.cached_tokens;
    }
    if (typeof inDetails.audio_tokens === "number") {
      usage.audioInputTokens = inDetails.audio_tokens;
    }
    if (typeof inDetails.text_tokens === "number") {
      usage.textInputTokens = inDetails.text_tokens;
    }
  }
  const outDetails = raw.output_token_details;
  if (outDetails) {
    if (typeof outDetails.audio_tokens === "number") {
      usage.audioOutputTokens = outDetails.audio_tokens;
    }
    if (typeof outDetails.text_tokens === "number") {
      usage.textOutputTokens = outDetails.text_tokens;
    }
  }
  return Object.keys(usage).length > 0 ? usage : null;
}

async function handleResponseDone(
  response: RealtimeResponseDone | undefined,
  ctx: RealtimeMessageContext
): Promise<void> {
  const usage = extractUsage(response?.usage);
  if (usage && ctx.recordUsage) {
    try {
      ctx.recordUsage(usage);
    } catch {
      // recordUsage is best-effort; never let it break the session loop.
    }
  }

  const calls = (response?.output ?? []).filter(isFunctionCall);
  for (const call of calls) {
    await dispatchFunctionCall(call, ctx);
  }
}

export async function handleRealtimeMessage(
  raw: string,
  ctx: RealtimeMessageContext
): Promise<void> {
  let message: RealtimeMessage;
  try {
    message = JSON.parse(raw) as RealtimeMessage;
  } catch {
    return;
  }

  switch (message.type) {
    case "input_audio_buffer.speech_started":
      ctx.send({ type: "speech_started" });
      return;
    case "input_audio_buffer.speech_stopped":
      ctx.send({ type: "speech_stopped" });
      // Reserve an ordered slot for this user turn now; its transcript completes
      // asynchronously and may land after the assistant has already replied.
      ctx.onTranscriptEvent?.({ kind: "user_turn" });
      return;
    case "response.output_audio_transcript.delta":
    case "response.audio_transcript.delta":
      ctx.send({ type: "audio_delta" });
      return;
    case "conversation.item.input_audio_transcription.completed":
      emitTranscript(ctx, "user", message.item_id, message.transcript);
      return;
    case "conversation.item.input_audio_transcription.failed":
      ctx.onTranscriptEvent?.({ kind: "user_transcript_failed" });
      return;
    case "response.output_audio_transcript.done":
    case "response.audio_transcript.done":
      emitTranscript(ctx, "assistant", message.item_id, message.transcript);
      return;
    case "output_audio_buffer.stopped":
    case "response.output_audio_buffer.stopped":
      ctx.send({ type: "audio_stopped" });
      return;
    case "response.done":
      await handleResponseDone(message.response, ctx);
      return;
    case "error":
      ctx.send({
        type: "error",
        message: message.error?.message ?? "Voice session error",
      });
      return;
    default:
      return;
  }
}
